import React, { memo, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { readDailyForceRecord, updateDailyForceRecord } from 'lib/daily-force-record'
import { useCurrentExercise } from 'store/workout/selectors'

import './punch-machine-challenge.scss'

const FORCE_STEPS = [200, 180, 160, 140, 120, 100, 90, 80, 70, 60, 50, 40, 30, 20, 10]
const COUNT_DURATION_MS = 6000
const RECORD_CALLOUT_DURATION_MS = 1800
// Repainting the glowing digits is the expensive part on a tablet, so the
// number is written at most this often instead of on every animation frame.
const DIGIT_UPDATE_INTERVAL_MS = 80

interface Props {
  onCountStart?: () => void
  onCountComplete?: () => void
}

const normalizeForce = (force: number): number =>
  Number.isFinite(force) ? Math.max(0, Math.round(force)) : 0

const formatForce = (force: number): string => force.toString().padStart(3, '0')

// Fast start, slow finish: the last kilograms arrive one by one to build tension.
const easeOutCubic = (progress: number): number => 1 - Math.pow(1 - progress, 3)

const activeStepCount = (force: number): number => FORCE_STEPS.filter((step) => force >= step).length

const useAnimatedForce = (
  force: number,
  punchKey: string,
  digitsRef: React.RefObject<HTMLSpanElement>,
  onCountStart?: () => void,
  onCountComplete?: () => void
) => {
  const targetForce = normalizeForce(force)
  // React state only changes when the ladder lights up another step or the
  // count completes; the digits themselves are written straight to the DOM.
  const [ladderForce, setLadderForce] = useState(0)
  const [completedPunchKey, setCompletedPunchKey] = useState<string | null>(null)
  const latestDigitsRef = useRef(formatForce(0))

  const writeDigits = (value: number) => {
    const text = formatForce(value)
    if (text === latestDigitsRef.current) return
    latestDigitsRef.current = text
    if (digitsRef.current) digitsRef.current.textContent = text
  }

  // Re-apply the latest digits after any React render so a re-render never
  // shows a stale number.
  useLayoutEffect(() => {
    if (digitsRef.current && digitsRef.current.textContent !== latestDigitsRef.current) {
      digitsRef.current.textContent = latestDigitsRef.current
    }
  })

  useEffect(() => {
    let animationFrame = 0
    let startTime: number | null = null
    let lastDigitUpdate = -Infinity
    let completed = false

    writeDigits(0)
    setLadderForce(0)
    setCompletedPunchKey(null)
    if (targetForce === 0) return

    onCountStart?.()

    const animate = (time: number) => {
      if (startTime === null) startTime = time

      const progress = Math.min(1, Math.max(0, (time - startTime) / COUNT_DURATION_MS))
      const nextForce =
        progress >= 1 ? targetForce : Math.max(0, Math.floor(targetForce * easeOutCubic(progress)))

      if (progress >= 1 || time - lastDigitUpdate >= DIGIT_UPDATE_INTERVAL_MS) {
        lastDigitUpdate = time
        writeDigits(nextForce)
        setLadderForce((current) => (activeStepCount(current) === activeStepCount(nextForce) ? current : nextForce))
      }

      if (progress < 1) {
        animationFrame = window.requestAnimationFrame(animate)
      } else {
        completed = true
        setLadderForce(targetForce)
        setCompletedPunchKey(punchKey)
        onCountComplete?.()
      }
    }

    animationFrame = window.requestAnimationFrame(animate)

    return () => {
      window.cancelAnimationFrame(animationFrame)
      if (!completed) onCountComplete?.()
    }
    // writeDigits only touches refs, so it is intentionally not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCountComplete, onCountStart, punchKey, targetForce])

  return { ladderForce, completedPunchKey }
}

interface ForceLadderProps {
  side: 'left' | 'right'
  force: number
  readyForPunch: boolean
}

const ForceLadder = memo(({ side, force, readyForPunch }: ForceLadderProps) => (
  <div
    className={`punch-machine__ladder punch-machine__ladder--${side}${
      readyForPunch ? ' punch-machine__ladder--ready' : ''
    }`}
    aria-hidden="true"
  >
    {FORCE_STEPS.map((step) => (
      <div
        className={`punch-machine__ladder-step${force >= step ? ' punch-machine__ladder-step--active' : ''}`}
        key={`${side}-${step}`}
      >
        <span>{step}</span>
      </div>
    ))}
  </div>
))

interface PendingRecord {
  punchKey: string
  force: number
}

export const PunchMachineChallenge = ({ onCountStart, onCountComplete }: Props) => {
  const { hits } = useCurrentExercise()
  const latestHit = hits[hits.length - 1]
  const latestForce = normalizeForce(latestHit?.force ?? 0)
  const punchKey = latestHit ? `${hits.length}-${latestHit.punchedAt}-${latestForce}` : 'empty'
  const digitsRef = useRef<HTMLSpanElement>(null)
  const { ladderForce, completedPunchKey } = useAnimatedForce(
    latestForce,
    punchKey,
    digitsRef,
    onCountStart,
    onCountComplete
  )

  const [dailyHigh, setDailyHigh] = useState(() => readDailyForceRecord().force)
  const [pendingRecord, setPendingRecord] = useState<PendingRecord | null>(null)
  const [isNewRecord, setIsNewRecord] = useState(false)
  const previousPunchKey = useRef<string | null>(null)

  const punchIsComplete = latestForce > 0 && completedPunchKey === punchKey
  const readyForPunch = latestForce === 0 || punchIsComplete
  const hasDailyHigh = dailyHigh > 0

  useEffect(() => {
    if (previousPunchKey.current === punchKey) return
    previousPunchKey.current = punchKey

    const record = readDailyForceRecord()
    const canSetRecord = latestForce > record.force
    setPendingRecord(canSetRecord ? { punchKey, force: latestForce } : null)
    setIsNewRecord(false)
  }, [latestForce, punchKey])

  useEffect(() => {
    if (!punchIsComplete || pendingRecord?.punchKey !== punchKey) return

    const { record, isNewRecord: didSetNewRecord } = updateDailyForceRecord(pendingRecord.force)
    setDailyHigh(record.force)
    if (!didSetNewRecord) return

    setIsNewRecord(true)
    const timeout = window.setTimeout(() => setIsNewRecord(false), RECORD_CALLOUT_DURATION_MS)
    return () => window.clearTimeout(timeout)
  }, [pendingRecord, punchIsComplete, punchKey])

  return (
    <section
      className={`punch-machine${isNewRecord ? ' punch-machine--new-record' : ''}`}
      data-testid="punch-machine-challenge"
      data-latest-force={latestForce}
      data-daily-high={dailyHigh}
      data-ready-for-punch={readyForPunch}
      aria-label="Power Punch single punch force challenge"
    >
      <div className="punch-machine__halo" aria-hidden="true" />
      <div className="punch-machine__sparks" aria-hidden="true">
        {new Array(12).fill(undefined).map((_, index) => (
          <i key={index} />
        ))}
      </div>

      <ForceLadder side="left" force={ladderForce} readyForPunch={readyForPunch} />
      <ForceLadder side="right" force={ladderForce} readyForPunch={readyForPunch} />

      <div className="punch-machine__cabinet">
        <div className="punch-machine__marquee">
          <span>★</span>
          <strong>POWER PUNCH</strong>
          <span>★</span>
        </div>

        <div className="punch-machine__scoreboard">
          <span className="punch-machine__score-label">LAST PUNCH</span>
          <div className="punch-machine__score">
            <span data-testid="animated-force" ref={digitsRef}>
              {formatForce(0)}
            </span>
            <small>KG</small>
          </div>
        </div>

        <div
          className={`punch-machine__daily-record${
            isNewRecord ? ' punch-machine__daily-record--new-record' : ''
          }`}
          aria-hidden={!hasDailyHigh}
        >
          {hasDailyHigh && (
            <>
              <span className="punch-machine__daily-record-label">DAILY HIGH</span>
              <span className="punch-machine__daily-record-score" aria-live="polite">
                <strong>{formatForce(dailyHigh)}</strong>
                <small>KG</small>
              </span>
            </>
          )}
        </div>

        <div className="punch-machine__callout" data-testid="record-callout" aria-live="polite">
          {isNewRecord ? 'NEW DAILY RECORD!' : ''}
        </div>
      </div>
    </section>
  )
}
